import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FormMode } from '../../common/form-mode';
import { WorkflowDataInterface } from '../workflow-data.interface';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { ChangeStepStateActionData } from 'src/app/components/designer/models/change-step-state-action-data';
import { State } from 'src/app/components/designer/models/state';
import { Step } from 'src/app/components/designer/models/step';
import { ContextualMenuEvent } from '../contextual-menu-event';
import { NeedSteps } from '../need-steps';

@Component({
    selector: 'app-workflow-action-change-step-state-data',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule, FaIconComponent, MatTooltipModule, MatCheckboxModule],
    templateUrl: './workflow-action-change-step-state-data.component.html',
    styleUrl: './workflow-action-change-step-state-data.component.scss'
})
export class WorkflowActionChangeStepStateDataComponent implements WorkflowDataInterface, OnInit, NeedSteps {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: ChangeStepStateActionData;

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    set data(value: string | ChangeStepStateActionData) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as ChangeStepStateActionData;
        }

        this.fillForm();
    }

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    get data(): ChangeStepStateActionData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    mode: FormMode;

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    public get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Source du menu contextuel.
     */
    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    /**
     * Liste des étapes du workflow.
     */
    public _steps: Step[];

    /**
     * Spécifie la liste des étapes du workflow.
     */
    set steps(value: Step[]) {
        this._steps = value;
        this.fillForm();
    }

    /**
     * Liste des états de l'étape selectionnée.
     */
    public _states: State[];

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Constructeur.
     */
    constructor() {}

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.dataForm = new UntypedFormGroup({
            step: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
            state: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
            activateStepIfNotStarted: new UntypedFormControl(false),
            activateStepIfCompleted: new UntypedFormControl(false),
        });

        if (this.mode === FormMode.Reading) {
            this.dataForm.disable();
        } else if (this.mode === FormMode.Creation || this.mode === FormMode.Edition) {
            this.dataForm.enable();
        }

        this.fillForm();
    }

    /**
     * Indique si le formulaire est valide.
     */
    public isValid(): boolean {
        return true;
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm) {
            if (this._data) {
                this.dataForm.get('step').setValue(this._data.stepId);
                this.dataForm.get('activateStepIfCompleted').setValue(this._data.activateStepIfCompleted);
                this.dataForm.get('activateStepIfNotStarted').setValue(this._data.activateStepIfNotStarted);

                if (this._states) {
                    this.dataForm.get('state').setValue(this._data.stateId);
                }

                if (this._steps) {
                    const selectedStep: Step = this._steps.find((x) => x.id == this._data.stepId);
                    this._states = selectedStep.data.states;
                    this.dataForm.get('state').setValue(this._data.stateId);
                }
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: ChangeStepStateActionData = new ChangeStepStateActionData();
        updatedData.stepId = this.dataForm.get('step').value;
        updatedData.stateId = this.dataForm.get('state').value;
        updatedData.activateStepIfCompleted = this.dataForm.get('activateStepIfCompleted').value;
        updatedData.activateStepIfNotStarted = this.dataForm.get('activateStepIfNotStarted').value;

        this._data = updatedData;
    }

    /**
     * Appellée lorsque l'utilisateur selectionne une étape.
     * @param event : évènement contenant l'étape selectionnée.
     */
    public onStepChanged(event: MatSelectChange) {
        const selectedStep: Step = this._steps.find((x) => x.id == event.value);
        this._states = selectedStep.data.states;
    }
}
